// Global coordinator for "major" prompts (notification permission, store review,
// any future OS permission). Enforces a 24h gap between them and stores state
// server-side in engagement_prompt_state. See plan: smarter-timing-for-prompts.
import { supabase } from '@/integrations/supabase/client';

export const MAJOR_PROMPT_GAP_MS = 24 * 60 * 60 * 1000;
export const REVIEW_COOLDOWN_MS = 120 * 24 * 60 * 60 * 1000;
export const NOTIF_PREPROMPT_MAX = 3;
export const NOTIF_RETRY_MS = 3 * 24 * 60 * 60 * 1000;

export interface PromptState {
  user_id: string;
  notif_preprompt_count: number;
  notif_preprompt_last_at: string | null;
  notif_preprompt_last_active_days: number;
  notif_os_decision: string | null;
  last_review_request_at: string | null;
  review_request_count: number;
  app_version_at_request: string | null;
  last_major_prompt_at: string | null;
}

export interface Eligibility {
  account_age_days: number;
  active_days: number;
  meaningful_action_count: number;
}

// Session-level lock so two prompts can't open in the same moment.
let sessionPromptAt = 0;

const db = supabase as any;

export async function getPromptState(userId: string): Promise<PromptState> {
  const { data } = await db.from('engagement_prompt_state').select('*').eq('user_id', userId).maybeSingle();
  if (data) return data as PromptState;
  const fresh = { user_id: userId };
  await db.from('engagement_prompt_state').upsert(fresh, { onConflict: 'user_id', ignoreDuplicates: true });
  return {
    user_id: userId, notif_preprompt_count: 0, notif_preprompt_last_at: null,
    notif_preprompt_last_active_days: 0, notif_os_decision: null, last_review_request_at: null,
    review_request_count: 0, app_version_at_request: null, last_major_prompt_at: null,
  };
}

export async function updatePromptState(userId: string, patch: Partial<PromptState>) {
  await db.from('engagement_prompt_state').upsert({ user_id: userId, ...patch }, { onConflict: 'user_id' });
}

export async function getEligibility(): Promise<Eligibility | null> {
  const { data, error } = await db.rpc('get_prompt_eligibility');
  if (error || !data) return null;
  return data as Eligibility;
}

export function majorPromptAllowed(state: PromptState): boolean {
  if (Date.now() - sessionPromptAt < MAJOR_PROMPT_GAP_MS) return false;
  if (state.last_major_prompt_at && Date.now() - new Date(state.last_major_prompt_at).getTime() < MAJOR_PROMPT_GAP_MS) return false;
  return true;
}

export async function markMajorPrompt(userId: string, extra: Partial<PromptState> = {}) {
  sessionPromptAt = Date.now();
  await updatePromptState(userId, { last_major_prompt_at: new Date().toISOString(), ...extra });
}

// Mark "just had a sensitive moment" (e.g. sign-in, error) – blocks prompts for this session tick.
let quietUntil = 0;
export function quietPrompts(ms = 60_000) { quietUntil = Date.now() + ms; }
export function isQuiet() { return Date.now() < quietUntil; }
